import { type CSSProperties, useEffect, useRef, useState } from "react";

const CANVAS_FEEDBACK_EVENT = "canvas-feedback";
const FEEDBACK_DURATION = 850;

interface CanvasFeedbackEventDetail {
  clientX: number;
  clientY: number;
  color?: string;
  outline?: string;
  message: string;
}

interface CanvasFeedbackState extends CanvasFeedbackEventDetail {
  id: number;
}

const contrastOutline = (color: string | undefined) => {
  const hex = color?.replace("#", "");
  if (!hex || !/^[0-9a-f]{6}$/i.test(hex)) return "#111111";

  const red = Number.parseInt(hex.slice(0, 2), 16);
  const green = Number.parseInt(hex.slice(2, 4), 16);
  const blue = Number.parseInt(hex.slice(4, 6), 16);
  const brightness = red * 0.299 + green * 0.587 + blue * 0.114;
  return brightness < 150 ? "#ffffff" : "#111111";
};

const showFeedback = (
  message: string,
  clientX: number,
  clientY: number,
  color?: string,
) => {
  const normalizedColor =
    color && color !== "transparent"
      ? color.startsWith("#")
        ? color
        : `#${color}`
      : undefined;

  window.dispatchEvent(
    new CustomEvent<CanvasFeedbackEventDetail>(CANVAS_FEEDBACK_EVENT, {
      detail: {
        clientX,
        clientY,
        color: normalizedColor,
        outline: contrastOutline(normalizedColor),
        message,
      },
    }),
  );
};

export const showColorDeselectFeedback = (
  clientX: number,
  clientY: number,
) => showFeedback("Color deselected!", clientX, clientY);

export const showColorPickFeedback = (
  clientX: number,
  clientY: number,
  color?: string,
) => showFeedback("Color picked!", clientX, clientY, color);

export const showPixelPlacedFeedback = (
  clientX: number,
  clientY: number,
  color?: string,
) => showFeedback("Pixel placed!", clientX, clientY, color);

export const ColorPickFeedback = () => {
  const [feedback, setFeedback] = useState<CanvasFeedbackState[]>([]);
  const nextId = useRef(0);
  const hideTimers = useRef(new Set<ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const handleFeedback = (event: Event) => {
      const { clientX, clientY, color, outline, message } = (
        event as CustomEvent<CanvasFeedbackEventDetail>
      ).detail;

      nextId.current += 1;
      const id = nextId.current;
      setFeedback((current) => [
        ...current,
        { id, clientX, clientY, color, outline, message },
      ]);

      const hideTimer = setTimeout(() => {
        hideTimers.current.delete(hideTimer);
        setFeedback((current) => current.filter((item) => item.id !== id));
      }, FEEDBACK_DURATION);
      hideTimers.current.add(hideTimer);
    };

    window.addEventListener(CANVAS_FEEDBACK_EVENT, handleFeedback);

    return () => {
      window.removeEventListener(CANVAS_FEEDBACK_EVENT, handleFeedback);
      hideTimers.current.forEach((timer) => clearTimeout(timer));
      hideTimers.current.clear();
    };
  }, []);

  if (feedback.length === 0) return null;

  return (
    <>
      {feedback.map((item) => (
        <div
          key={item.id}
          aria-live="polite"
          className="color-picked-feedback"
          role="status"
          style={
            {
              "--feedback-outline": item.outline,
              color: item.color,
              left: item.clientX,
              top: item.clientY,
            } as CSSProperties
          }
        >
          {item.message}
        </div>
      ))}
    </>
  );
};
